import { useEffect, useId, useRef, useState } from 'react';
import { GEMINI_DIRECTION_GROUPS } from '../../supabase/functions/_shared/gemini-directions';

/** One independent choice; saved basic/subtle combinations are never rewritten on mount. */
const REMOVED_EMOTIONS = new Set(['Jealous','Matter-of-fact','Persuasive','Respectful','Sincere','Authoritative','Ironic','Amused','Nervous','Vulnerable','Excited','Tender','Apprehensive','Reassuring']);
export const EMOTION_OPTIONS = [...new Set([
  ...GEMINI_DIRECTION_GROUPS.emotion.options,
  ...GEMINI_DIRECTION_GROUPS.subtle.options,
  ...GEMINI_DIRECTION_GROUPS.attitude.options,
])].filter(emotion=>emotion!=='Neutral'&&!REMOVED_EMOTIONS.has(emotion)).sort((a,b)=>a.localeCompare(b,'en'));
interface Props {
  selectionKey: string;
  emotion?: string;
  subtle?: string;
  disabled: boolean;
  onChoose: (emotion: string | undefined, subtle: string | undefined) => void;
}
export default function EmotionListbox(props: Props) {
  const id=useId(), selected=props.subtle??props.emotion;
  // Keep legacy saved attributes intact, without reintroducing retired choices.
  const options=[...new Set([...EMOTION_OPTIONS,...[props.emotion,props.subtle].filter((s):s is string=>!!s)])].filter(emotion=>emotion!=='Neutral'&&!REMOVED_EMOTIONS.has(emotion)).sort((a,b)=>a.localeCompare(b,'en'));
  const initialActive=selected&&options.includes(selected)?selected:options[0];
  const [active,setActive]=useState(initialActive);
  const buttons=useRef(new Map<string,HTMLButtonElement>());
  const search=useRef({text:'',at:0});
  useEffect(()=>{setActive(initialActive);search.current={text:'',at:0};},[props.selectionKey,initialActive]);
  return <div className="performance-emotion-picker">
    <div className="performance-pane-heading"><p id={`${id}-label`} className="performance-label">Emotion</p>
      <button type="button" className="performance-clear performance-neutral" aria-label="Emotion: Neutral"
        aria-pressed={selected==='Neutral'} disabled={props.disabled} title="Neutral emotion; preserves delivery, volume, speed and pitch"
        onPointerDown={event=>{if(event.button===0)event.preventDefault();}} onClick={()=>props.onChoose('Neutral',undefined)}>Neutral</button>
    </div>
    <div role="listbox" aria-labelledby={`${id}-label`} aria-disabled={props.disabled} className="performance-emotion-listbox">
      {options.map((emotion,index)=><button type="button" role="option" key={emotion}
        ref={node=>{if(node)buttons.current.set(emotion,node);else buttons.current.delete(emotion);}}
        className="performance-emotion-option" aria-label={`Emotion: ${emotion}`} title={emotion}
        aria-selected={selected===emotion} disabled={props.disabled} tabIndex={active===emotion?0:-1}
        onFocus={()=>setActive(emotion)}
        onPointerDown={event=>{if(event.button===0)event.preventDefault();}}
        onClick={()=>props.onChoose((GEMINI_DIRECTION_GROUPS.emotion.options as readonly string[]).includes(emotion)?emotion:undefined,
          (GEMINI_DIRECTION_GROUPS.emotion.options as readonly string[]).includes(emotion)?undefined:emotion)}
        onKeyDown={event=>{
          let next:number|undefined;
          if(event.key==='Home')next=0;
          else if(event.key==='End')next=options.length-1;
          else if(event.key==='ArrowDown')next=Math.min(options.length-1,index+1);
          else if(event.key==='ArrowUp')next=Math.max(0,index-1);
          else if(event.key==='ArrowRight'||event.key==='ArrowLeft')next=index;
          else if(event.key.length===1&&event.key!==' '&&!event.ctrlKey&&!event.metaKey&&!event.altKey){
            const now=Date.now();search.current={text:(now-search.current.at<700?search.current.text:'')+event.key.toLowerCase(),at:now};
            next=options.findIndex(name=>name.toLowerCase().startsWith(search.current.text));
          }
          if(next!==undefined){event.preventDefault();if(next>=0)buttons.current.get(options[next])?.focus();}
        }}>{emotion}</button>)}
    </div>
  </div>;
}
